import { z } from 'zod';

export const TextNodeSchema = z.object({
  type: z.literal('text'),
  text: z.string(),
});
export type TextNode = z.infer<typeof TextNodeSchema>;

const ExternalLinkAttributesSchema = z
  .object({
    to: z.string().url(),
    external: z.boolean().optional(),
  })
  .strict();

const InternalLinkAttributesSchema = z
  .object({
    targetScreenId: z.string(),
  })
  .strict();

const LinkAttributesSchema = z.union([
  InternalLinkAttributesSchema,
  ExternalLinkAttributesSchema,
]);

export type LinkAttributes = z.infer<typeof LinkAttributesSchema>;

export type ParagraphNode = { type: 'paragraph'; children: ASTNode[] };
export type LinkNode = {
  type: 'link';
  attributes: LinkAttributes;
  children: ASTNode[];
};
export type ScrollViewNode = {
  type: 'scrollview';
  attributes?: { maxBlockSize?: number };
  children: ASTNode[];
};

export type ASTNode = TextNode | ParagraphNode | LinkNode | ScrollViewNode;

export const ASTNodeSchema: z.ZodType<ASTNode> = z.lazy(() =>
  z.discriminatedUnion('type', [
    TextNodeSchema,
    ParagraphNodeSchema,
    LinkNodeSchema,
    ScrollViewNodeSchema,
  ]),
);

export const ParagraphNodeSchema = z.object({
  type: z.literal('paragraph'),
  children: z.array(ASTNodeSchema).min(1),
});

export const LinkNodeSchema = z.object({
  type: z.literal('link'),
  attributes: LinkAttributesSchema,
  children: z.array(ASTNodeSchema).min(1),
});

export const ScrollViewNodeSchema = z.object({
  type: z.literal('scrollview'),
  attributes: z
    .object({
      maxBlockSize: z.number().int().nonnegative().finite().optional(),
    })
    .optional(),
  children: z.array(ASTNodeSchema).min(1),
});

export const ASTSchema = z.object({
  children: z.array(ASTNodeSchema).min(1),
});

export const ScreenSchema = z.object({
  id: z.string(),
  children: z.array(ASTNodeSchema).min(1),
});

export type Screen = z.infer<typeof ScreenSchema>;

function collectTargetScreenIds(nodes: ASTNode[]): string[] {
  const ids: string[] = [];
  for (const node of nodes) {
    if (node.type === 'link' && 'targetScreenId' in node.attributes) {
      ids.push(node.attributes.targetScreenId);
    }
    if ('children' in node) {
      ids.push(...collectTargetScreenIds(node.children));
    }
  }
  return ids;
}

export const ScreenDocumentSchema = z
  .object({
    screens: z.array(ScreenSchema).min(1),
  })
  .refine(
    (doc) => {
      const ids = doc.screens.map((s) => s.id);
      return new Set(ids).size === ids.length;
    },
    { message: 'Duplicate screen IDs' },
  )
  .refine(
    (doc) => {
      const screenIds = new Set(doc.screens.map((s) => s.id));
      const targets = doc.screens.flatMap((s) =>
        collectTargetScreenIds(s.children),
      );
      return targets.every((id) => screenIds.has(id));
    },
    { message: 'targetScreenId references a non-existent screen' },
  );

export function parseScreens(jsonContent: unknown): Screen[] | null {
  if (jsonContent == null) return null;

  const screenResult = ScreenDocumentSchema.safeParse(jsonContent);
  if (screenResult.success) {
    return screenResult.data.screens;
  }

  const legacyResult = ASTSchema.safeParse(jsonContent);
  if (legacyResult.success) {
    return [{ id: 'default', children: legacyResult.data.children }];
  }

  return null;
}
